import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import {
  DataGrid,
  type DataGridProps,
  type GridColDef,
  type GridPaginationModel,
  type GridRowSelectionModel,
  type GridSortModel,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { trTR } from '@mui/x-data-grid/locales';
import type { ReactNode } from 'react';
import { EmptyContent } from './empty-content.js';

// MUI X ships an incomplete Turkish locale — 87 keys are commented out
// upstream, so they silently fall back to English. These are the ones that
// actually surface in a list screen; the rest belong to charts, pivot and the
// AI panel, which we do not use. Re-check after a @mui/x-data-grid upgrade.
const TR_LOCALE_GAPS = {
  paginationDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
    `${from}-${to} / ${count === -1 ? `${to}+` : count}`,
  paginationItemAriaLabel: (type: 'first' | 'last' | 'next' | 'previous') =>
    ({
      first: 'İlk sayfaya git',
      last: 'Son sayfaya git',
      next: 'Sonraki sayfaya git',
      previous: 'Önceki sayfaya git',
    })[type],
  noColumnsOverlayLabel: 'Sütun yok',
  noColumnsOverlayManageColumns: 'Sütunları yönet',
  headerFilterClear: 'Filtreyi temizle',
  columnMenuAriaLabel: (columnName: string) => `${columnName} sütun menüsü`,
} as const;

const TR_LOCALE_TEXT = {
  ...trTR.components.MuiDataGrid.defaultProps.localeText,
  ...TR_LOCALE_GAPS,
};

export type { GridColDef };

export type BtsisDataGridProps<Row extends GridValidRowModel> = Omit<
  DataGridProps<Row>,
  'rows' | 'columns' | 'paginationModel' | 'onPaginationModelChange' | 'slots'
> & {
  rows: readonly Row[];
  columns: readonly GridColDef<Row>[];
  rowCount: number;
  loading?: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  selectionModel?: GridRowSelectionModel;
  onSelectionModelChange?: (model: GridRowSelectionModel) => void;
  emptyTitle: string;
  emptyDescription?: string;
  toolbar?: ReactNode;
  height?: number | string;
};

// Server-driven by construction: paginationMode and sortingMode are always
// 'server', so a list screen cannot accidentally fetch every row and filter in
// the browser. See .claude/rules/list-screen-pattern.md.
export function BtsisDataGrid<Row extends GridValidRowModel>({
  rows,
  columns,
  rowCount,
  loading = false,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  selectionModel,
  onSelectionModelChange,
  emptyTitle,
  emptyDescription,
  toolbar,
  height = 600,
  ...other
}: BtsisDataGridProps<Row>) {
  return (
    <Card>
      {toolbar}
      <Box sx={{ height, width: '100%' }}>
        <DataGrid<Row>
          rows={rows as Row[]}
          columns={columns as GridColDef<Row>[]}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={onSelectionModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          localeText={TR_LOCALE_TEXT}
          disableColumnMenu={false}
          disableRowSelectionOnClick
          slots={{
            noRowsOverlay: () => (
              <EmptyContent title={emptyTitle} description={emptyDescription} sx={{ height: 1 }} />
            ),
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { bgcolor: 'background.neutral' },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          }}
          {...other}
        />
      </Box>
    </Card>
  );
}
